import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, EMPTY, Observable, of, Subject } from 'rxjs';
import { catchError, filter, finalize, map } from 'rxjs/operators';

import { apiUrls, OrderModelType } from '../../../../constants';
import { guid } from '../../../../tools';
import { BaseService, DesktopActiveSymbolService } from '../../../shared/services';
import { SIDE } from '../../../swipe';
import { OrderSending } from './order-sending';
import { OrderValidation } from './order-validation';

import { IOrder, IOrderFromMarketDepth, IOrderFromUrl } from '../../../../modules/order';
import { MarketDepthStateType, OMSOrder, OmsOrderStates } from '../../../../modules/order';
import { OrderActionType, OrderPlaceResponseDto, OrderStructureType, Side } from '../../../../modules/order';
import { IBaseOrderEntity, IDeleteResponseModel, IOMSOrderGetRespons } from '../../../../modules/order';
import { BaseOrderResponse, EasyState, EntryType, From, IBaseOMSOrder } from '../../../../modules/order';
import { SettingStore } from '@easy-lib/state/setting/setting.store';

@Injectable({ providedIn: 'root' })
export class OrderService extends BaseService {
	private orderSending = inject(OrderSending);
	private settingStore = inject(SettingStore);
	private orderValidationService = inject(OrderValidation);
	private activeSymbolService = inject(DesktopActiveSymbolService);

	showOrderFormSubject = new Subject<boolean>();
	showOrderForm$ = this.showOrderFormSubject.asObservable();

	public deleteDraftSubject = new Subject<IOrder>();
	deleteDraftSubject$ = this.deleteDraftSubject.asObservable();

	callDeleteOmsOrderFromOutsideOfComponentSubject = new Subject<string>();
	callDeleteOmsOrderFromOutsideOfComponent$ = this.callDeleteOmsOrderFromOutsideOfComponentSubject.asObservable();

	openOrderFormEditModeSubject = new Subject<IBaseOrderEntity>();
	openOrderFormEditModeObservable$ = this.openOrderFormEditModeSubject.asObservable();
	get orderUrl(): string {
		return this.mtsPath + apiUrls.oms;
	}

	orderOverlayEnable = false;

	private orderOverlaySubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(this.orderOverlayEnable);
	public orderOverlayState$ = this.orderOverlaySubject.asObservable();

	protected ordersSubject = new BehaviorSubject<IBaseOrderEntity[]>([]);
	public orders$ = this.ordersSubject.asObservable();

	protected orderGetCalledSub = new Subject<boolean>();
	public orderGetCalledObs$ = this.orderGetCalledSub.asObservable();

	makeOrderFromStateObservable = (expectedType: MarketDepthStateType) =>
		this.orderFromMarketDepthSubject.pipe(filter(res => res?.type === expectedType));

	orderFromMarketDepthSubject = new Subject<Partial<IOrderFromMarketDepth>>();

	public orderFromMarketDepthSubject$ = this.makeOrderFromStateObservable(MarketDepthStateType.OrderFrom);
	public orderFromDesktopMarketDepth$ = this.makeOrderFromStateObservable(MarketDepthStateType.OrderFromDesktop);
	public editOrderFromMarketDepthSubject$ = this.makeOrderFromStateObservable(MarketDepthStateType.EditOrder);
	public orderFromSparkLine$ = this.makeOrderFromStateObservable(MarketDepthStateType.SparkLine);
	public orderFromLayouts$ = this.makeOrderFromStateObservable(MarketDepthStateType.Layouts);
	public orderFromContextMenu$ = this.makeOrderFromStateObservable(MarketDepthStateType.ContextMenu);
	public orderFromPopup$ = this.makeOrderFromStateObservable(MarketDepthStateType.Popup);
	public orderFromSearchPanel$ = this.makeOrderFromStateObservable(MarketDepthStateType.SearchPanel);
	public orderFromCloneOrder$ = this.makeOrderFromStateObservable(MarketDepthStateType.CloneOrder);

	readQuantityFromSettings(side: Side): number {
		let quantity = 0;
		if (side === Side.Buy) quantity = this.settingStore.setting().buyQuantity;
		else if (side === Side.Sell) quantity = this.settingStore.setting().sellQuantity;
		return quantity;
	}
	orderOverlayChangeState(isOpen: boolean): void {
		if (isOpen) {
			setTimeout(() => {
				this.orderOverlayEnable = isOpen;
				this.orderOverlaySubject.next(isOpen);
			}, 100);
		} else {
			this.orderOverlayEnable = isOpen;
			this.orderOverlaySubject.next(isOpen);
		}
	}
	getDefaultOrderInputData(side: string, quantity: number, price: number, isin: string = null): IOrderFromUrl {
		const defaultOrderInputData: IOrderFromUrl = {
			isin: isin ?? this.activeSymbolService.activeStockIsin,
			side,
			quantity: quantity,
			price: price ? price : null,
			editOrMake: OrderActionType.make,
			draftOrRegular: OrderStructureType.regular,
			id: null,
			validityType: null,
			validityDate: null,
			iceberg: null,
			financialProviderCode: null,
			isEditMode: false,
			quantityEntryType: quantity ? EntryType.Other : EntryType.Default
		};
		return defaultOrderInputData;
	}
	getOrderDataForEdit(order: IBaseOrderEntity): IOrderFromUrl {
		const orderInputData: IOrderFromUrl = <IOrderFromUrl>{
			isin: order.symbolIsin,
			side: order.side === Side.Buy ? SIDE.BUY : SIDE.SELL,
			quantity: order.quantity,
			price: order.price,
			editOrMake: OrderActionType.edit,
			draftOrRegular:
				order.orderModelType === OrderModelType.draft ? OrderStructureType.draft : OrderStructureType.regular,
			id: order.id,
			validityType: order.validityType,
			validityDate: order.validityDate ? order.validityDate : '',
			iceberg: '',
			isEditMode: true
		};
		if (order.orderModelType === OrderModelType.oms && order?.ExecutedQuantity > 0)
			orderInputData.quantity = order.quantity - order?.ExecutedQuantity;
		return orderInputData;
	}
	updateOrderFromMarketDepthState(order: Partial<IOrderFromMarketDepth>) {
		this.orderFromMarketDepthSubject.next(order);
	}
	prepareOmsOrderToCreate(order: IBaseOMSOrder) {
		const result = this.orderValidationService.doBasicOrderValidations(order);
		if (!result.successful) {
			return { isSuccess: result.successful, errorMessage: result.errorMessage };
		}

		const newOrder = new OMSOrder({
			...order,
			id: guid(),
			createDateTime: new Date().toString(),
			easyState: EasyState.BeingCreated
		});
		const sendingOrder = this.orderSending.prepareSendingOrder(order);
		return { isSuccess: result.successful, newOrder, sendingOrder };
	}
	prepareOmsOrderToUpdate(order: IBaseOrderEntity) {
		const result = this.orderValidationService.doBasicOrderValidations(order);
		if (!result.successful) {
			return { isSuccess: result.successful, errorMessage: result.errorMessage };
		}

		const sendingOrder = {
			...this.orderSending.prepareSendingOrder(order),
			id: null,
			parentId: order.id
		} as IBaseOMSOrder;
		const newOrder = new OMSOrder({
			...order,
			...sendingOrder,
			trades: [],
			easyState: EasyState.InModifyQueue,
			orderState: OmsOrderStates.OnSending,
			createDateTime: new Date(Date.now()).toLocaleString(),
			id: guid()
		});

		return { isSuccess: result.successful, newOrder, sendingOrder };
	}
	getOmsOrderPlaceInfo(ids: string[], actionType: 'get' | 'push' = 'get') {
		return this.httpService
			.get<OrderPlaceResponseDto>(
				this.mtsPath + apiUrls.marketSheet + `place?orderIds=${ids.join(',')}&actionType=${actionType}`
			)
			.pipe(
				map(res => res.orderPlaces),
				catchError(() => EMPTY)
			);
	}
	//----------------- http requests normal ---------------
	getOrders(): Observable<IOMSOrderGetRespons> {
		return this.httpService.get<IOMSOrderGetRespons>(this.orderUrl + 'order', { guestUserRestriction: true }).pipe(
			// map(res => res.orders.map(t => deleteProperties(t, this.exceptedOmsProps))),
			// map(res => res.map(t => new OMSOrder({ ...t, easyState: EasyState.Done }))),
			catchError(() => {
				return of({} as IOMSOrderGetRespons);
			}),
			finalize(() => {
				this.orderGetCalledSub.next(true);
			})
		);
	}
	createOrder(order: IBaseOMSOrder) {
		return this.httpService
			.post<BaseOrderResponse>(this.orderUrl + 'v2/order', {
				body: { order }
			})
			.pipe(
				catchError(error => {
					return of({ isSuccessful: false, message: error?.error } as BaseOrderResponse);
				})
			);
	}
	updateOrder(order: IBaseOMSOrder): Observable<BaseOrderResponse> {
		return this.httpService
			.put<BaseOrderResponse>(this.orderUrl + 'v2/order', {
				body: {
					modifyOrder: { ...order }
				}
			})
			.pipe(catchError(error => of({ isSuccessful: false, message: error?.error } as BaseOrderResponse)));
	}
	deleteOrder(id: string, from: From.OrbisMobile | From.OrbisDesktop) {
		return this.httpService
			.delete<IDeleteResponseModel>(`${this.orderUrl + 'v2/order'}/${id}/${from}`)
			.pipe(catchError(error => of({ isSuccessful: false, message: error?.error } as BaseOrderResponse)));
	}
	//------------------http requests option---------------
}
